import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { ShoppingBag, Loader2, Search, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { SHOPS } from "@/lib/config";
import { toast } from "sonner";

const INITIAL_LIMIT = 8;
const DESC_LIMIT = 90;

export default function Products() {
  const [shop, setShop] = useState("moby_shop");
  const [subcategory, setSubcategory] = useState("all");
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [expandedDesc, setExpandedDesc] = useState({});
  const { add } = useCart();

  useEffect(() => {
    setLoading(true);
    setShowAll(false);
    api
      .listProducts(shop)
      .then((data) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [shop]);

  // Reset "show all" when filters change
  useEffect(() => {
    setShowAll(false);
  }, [subcategory, query]);

  const subcategories = useMemo(() => {
    const set = new Set(products.map((p) => p.subcategory));
    return ["all", ...Array.from(set).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    let list = subcategory === "all" ? products : products.filter((p) => p.subcategory === subcategory);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.subcategory.toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, subcategory, query]);

  const visible = useMemo(
    () => (showAll ? filtered : filtered.slice(0, INITIAL_LIMIT)),
    [filtered, showAll]
  );
  const hiddenCount = filtered.length - visible.length;

  const handleAdd = (p) => {
    add(p);
    toast.success(`${p.name} dodano u košaricu`);
  };

  return (
    <section
      id="proizvodi"
      data-testid="products-section"
      className="relative py-24 lg:py-32 bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[#D32F2F] uppercase tracking-[0.25em] text-xs font-semibold mb-3">
              Naša ponuda
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-neutral-900"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Proizvodi
            </h2>
          </div>

          <Tabs value={shop} onValueChange={(v) => { setShop(v); setSubcategory("all"); setQuery(""); }}>
            <TabsList className="bg-white border border-neutral-200 p-1.5 h-auto rounded-full">
              {SHOPS.map((s) => (
                <TabsTrigger
                  key={s.key}
                  value={s.key}
                  data-testid={`shop-tab-${s.key}`}
                  className="rounded-full px-6 py-2.5 data-[state=active]:bg-[#D32F2F] data-[state=active]:text-white text-neutral-700 font-medium"
                >
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Search bar */}
        <div className="relative max-w-xl mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          <Input
            data-testid="product-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pretraži proizvode (npr. mobitel, pernica, baterija…)"
            className="pl-11 pr-10 h-12 rounded-full border-neutral-200 bg-white shadow-sm focus-visible:ring-[#D32F2F]"
          />
          {query && (
            <button
              data-testid="product-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-500"
              aria-label="Očisti pretragu"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Subcategory pills */}
        {subcategories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {subcategories.map((s) => (
              <button
                key={s}
                onClick={() => setSubcategory(s)}
                data-testid={`subcat-${s}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  subcategory === s
                    ? "bg-neutral-900 text-white"
                    : "bg-white border border-neutral-200 text-neutral-700 hover:border-[#D32F2F] hover:text-[#D32F2F]"
                }`}
              >
                {s === "all" ? "Sve" : s}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="py-20 grid place-items-center text-neutral-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="products-empty">
            <div className="text-6xl mb-4">{query ? "🔍" : "📦"}</div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-2">
              {query
                ? `Nema rezultata za „${query}"`
                : "Još nema proizvoda u ovoj kategoriji"}
            </h3>
            <p className="text-neutral-500">
              {query
                ? "Pokušajte sa drugim pojmom ili pogledajte sve proizvode."
                : "Administrator može dodati proizvode preko admin panela."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {visible.map((p, i) => {
                const isExpanded = expandedDesc[p.id];
                const isLong = p.description.length > DESC_LIMIT;
                const displayDesc = isExpanded || !isLong
                  ? p.description
                  : p.description.slice(0, DESC_LIMIT).trimEnd() + "…";
                return (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: (i % 8) * 0.05 }}
                    data-testid={`product-card-${p.id}`}
                    className="product-card group bg-white rounded-2xl overflow-hidden border border-neutral-200/70 flex flex-col"
                  >
                    <div className="aspect-square overflow-hidden bg-neutral-100 relative">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-md text-xs font-medium px-3 py-1 rounded-full text-neutral-700">
                        {p.subcategory}
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-bold text-neutral-900 text-lg mb-1 line-clamp-1">
                        {p.name}
                      </h3>
                      <p className="text-sm text-neutral-500 mb-2 whitespace-pre-wrap">
                        {displayDesc}
                        {isLong && (
                          <button
                            type="button"
                            data-testid={`desc-toggle-${p.id}`}
                            onClick={() =>
                              setExpandedDesc((prev) => ({ ...prev, [p.id]: !prev[p.id] }))
                            }
                            className="ml-1 text-[#D32F2F] font-semibold hover:underline"
                          >
                            {isExpanded ? "manje" : "više"}
                          </button>
                        )}
                      </p>
                      <div className="flex items-center justify-between mt-auto pt-3">
                        <div
                          className="text-2xl font-extrabold text-[#D32F2F]"
                          style={{ fontFamily: "Cabinet Grotesk" }}
                        >
                          {p.price.toFixed(2)} KM
                        </div>
                        <button
                          onClick={() => handleAdd(p)}
                          data-testid={`add-to-cart-${p.id}`}
                          className="bg-neutral-900 hover:bg-[#D32F2F] text-white rounded-full p-3 transition-colors"
                          aria-label="Dodaj u košaricu"
                        >
                          <ShoppingBag className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>

            {/* Show more button */}
            {hiddenCount > 0 && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={() => setShowAll(true)}
                  data-testid="show-more-btn"
                  className="group inline-flex items-center gap-3 bg-white hover:bg-[#D32F2F] hover:text-white text-neutral-900 border border-neutral-200 hover:border-[#D32F2F] px-7 py-3 rounded-full font-semibold transition-all shadow-sm hover:shadow-lg"
                >
                  Prikaži ostale artikle
                  <span className="text-xs bg-[#D32F2F] group-hover:bg-white text-white group-hover:text-[#D32F2F] rounded-full px-2 py-0.5 font-bold">
                    +{hiddenCount}
                  </span>
                  <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                </button>
              </div>
            )}

            {showAll && filtered.length > INITIAL_LIMIT && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={() => setShowAll(false)}
                  data-testid="show-less-btn"
                  className="text-sm text-neutral-500 hover:text-[#D32F2F] font-medium"
                >
                  ↑ Prikaži manje
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
